import { Injectable } from '@angular/core';
import { GetConnexBookingsStatsRequest, GetConnexBookingsStatsResponse } from 'app/models';
import { MessageService } from 'primeng/api';
import { ApiHttp } from 'app/providers';

@Injectable()
export class DeliveryOrderService {
    constructor(private apiHttp: ApiHttp) {
    }

    public changeDeliveryStatus(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/ChangeDeliveryStatus', data, messageService);
    }

    public assignScheduledOrderToRider(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/AssignScheduledOrderToRider', data, messageService);
    }

    public offerOrderToRider(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/OfferOrderToRider', data, messageService);
    }

    public getNearbyDriversForAssign(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetNearbyDriversForAssign', data, messageService);
    }

    public getDeliveryOrders(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetDeliveryOrders', data, messageService);
    }

    public getConnexDeliveryOrders(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetConnexDeliveryOrders', data, messageService);
    }

    public getConnexBookingsStats(
        data: GetConnexBookingsStatsRequest,
        messageService?: MessageService
    ): Promise<GetConnexBookingsStatsResponse> {
        return this.apiHttp.post(
            'api/AdminDeliveryOrder/GetConnexBookingsStats',
            data,
            messageService
        ) as Promise<GetConnexBookingsStatsResponse>;
    }

    public getDeliveryOrderDetail(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetOrderDetail', data, messageService);
    }

    public getOrderPricingBreakdown(data: any, messageService?: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetOrderPricingBreakdown', data, messageService);
    }

    public getTransactions(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetTransactions', data, messageService);
    }

    public getGuaranteedPay(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetGuaranteedPay', data, messageService);
    }

    public getTopUps(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetTopUps', data, messageService);
    }

    public getCashOut(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetCashOut', data, messageService);
    }

    public exportTransactions(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp
            .postDownloadFile('api/AdminDeliveryOrder/ExportTransactionsStreamed', data, messageService, true)
            .then(async response => {
                const contentType = response?.headers?.get('Content-Type') || '';
                if (response?.ok && /json/i.test(contentType)) {
                    let message = 'Export returned JSON instead of a downloadable file.';
                    try {
                        const errorBody = await (response.body as Blob).text();
                        const parsed = JSON.parse(errorBody);
                        message = parsed.ErrorMessage || parsed.Message || message;
                    } catch {
                        // Keep the actionable fallback when the JSON body is malformed.
                    }
                    return { Success: false, ErrorMessage: message };
                }
                return {
                    Success: !!response?.ok,
                    ErrorMessage: response?.ErrorMessage,
                    HttpResponse: response
                };
            });
    }

    public exportBookings(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.postDownloadFile('api/AdminDeliveryOrder/ExportBookings', data, messageService);
    }

    public getRiderDeliveryOrders(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetRiderDeliveryOrders', data, messageService);
    }

    public getRiderDeliveryOrderDetail(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetRiderDeliveryOrderDetail', data, messageService);
    }

    public getRiderWalletInfo(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetRiderWalletInfo', data, messageService);
    }

    public getLiveMapActiveOrders(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetLiveMapActiveOrders', data, messageService);
    }

    public getLiveMapActiveOrderDetails(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetLiveMapActiveOrderDetails', data, messageService);
    }

    public prioritizeOrder(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/PrioritizeOrder', data, messageService);
    }

    public getOrderDetailsByTransactionId(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetOrderDetailsByTransactionId', data, messageService);
    }

    public cancelDeliveryOrder(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/CancelDeliveryOrder', data, messageService);
    }

    public completeOrder(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/CompleteOrder', data, messageService);
    }

    public getRiderAssigningHistory(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetRiderAssigningHistory', data, messageService);
    }

    public getCustomerStatistics(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetCustomerStatistics', data, messageService);
    }

    public getLiveMapTeamStatisticts(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetLiveMapTeamStatisticts', data, messageService);
    }

    public calculateLiveMapTeamStatistics(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/CalculateLiveMapTeamStatistics', data, messageService);
    }

    public getOrdersCompletedAndUnallocated(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.post('api/AdminDeliveryOrder/GetOrdersCompletedAndUnallocated', data, messageService);
    }

    public exportOrdersCompletedAndUnallocated(data: any, messageService: MessageService): Promise<any> {
        return this.apiHttp.postDownloadFile('api/AdminDeliveryOrder/ExportOrdersCompletedAndUnallocated', data, messageService);
	}

	public uploadHailedTaxiOrderFromXls(data: any, messageService: MessageService): Promise<any> {
		return this.apiHttp.post('api/import/UploadHailedTaxiOrderFromXls', data, messageService);
	}
}
